import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_BASE_URL } from '../config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem('moneyball_user');
        const savedToken = localStorage.getItem('moneyball_access_token');

        if (savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);

          // If we have a saved token, use it; otherwise refresh
          if (savedToken) {
            setAccessToken(savedToken);
          } else {
            // Try to refresh token to get a new access token
            await refreshAccessToken();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid auth data
        localStorage.removeItem('moneyball_user');
        localStorage.removeItem('moneyball_access_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password, tenantId = process.env.REACT_APP_DEFAULT_TENANT_ID || 'mbco') => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: sends/receives cookies
        body: JSON.stringify({ email, password, tenant_id: tenantId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();

      // Store user data and access token
      setUser(data.user);
      setAccessToken(data.access_token);

      // Persist user data and access token to localStorage
      localStorage.setItem('moneyball_user', JSON.stringify(data.user));
      localStorage.setItem('moneyball_access_token', data.access_token);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }

      const data = await response.json();

      // Store user data and access token
      setUser(data.user);
      setAccessToken(data.access_token);

      // Persist user data and access token to localStorage
      localStorage.setItem('moneyball_user', JSON.stringify(data.user));
      localStorage.setItem('moneyball_access_token', data.access_token);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout to clear refresh token cookie
      if (accessToken) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem('moneyball_user');
      localStorage.removeItem('moneyball_access_token');
      localStorage.removeItem('moneyball_demo_state');
    }
  };

  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Sends refresh token cookie
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      setAccessToken(data.access_token);

      // Persist new access token to localStorage
      localStorage.setItem('moneyball_access_token', data.access_token);

      return data.access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, log out the user
      await logout();
      throw error;
    }
  };

  const getAccessToken = async () => {
    // If token exists and is not expired (basic check), return it
    if (accessToken) {
      return accessToken;
    }

    // Otherwise, try to refresh
    return await refreshAccessToken();
  };

  const value = {
    user,
    accessToken,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    getAccessToken,
    refreshAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
